import { useMemo, useState } from "react";
import { IntlShape, useIntl } from "react-intl";

import { ColumnDefinition, TABLE_SELECTION_COLUMN_WIDTH } from "@nens/ranalib";

import { FileDataTypeEnum, FileFeatureTypeEnum } from "@/lib/rana-file-data-type";
import { isTypedDescriptor } from "@/utils/is-descriptor-type";

import { getFiles } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { CompleteFile } from "../../../types/file-reference-with-file";
import { getFileName } from "../../../utils/files";

export type FilesTableMode = "layers" | "study-area";

const getDataType = (file: CompleteFile) => {
  const { dataType } = file.file.descriptor;
  return dataType as FileDataTypeEnum | undefined;
};

const isAbleToBeVisualized = (file: CompleteFile) => {
  const dataType = getDataType(file);
  return dataType === "raster" || dataType === "vector" || dataType === "scenario";
};

const isModelSchematisation = (file: CompleteFile) => {
  const dataType = getDataType(file);
  return dataType === "threedi_schematisation";
};

const isStudyAreaFile = (file: CompleteFile) => {
  const { descriptor } = file.file;
  return (
    isTypedDescriptor(descriptor, "vector") &&
    descriptor.meta?.featureTypeDefinition === FileFeatureTypeEnum.StudyArea
  );
};

const getDisabledReason = ({
  intl,
  file,
  mode,
}: {
  intl: IntlShape;
  file: CompleteFile;
  mode: FilesTableMode;
}) => {
  if (file.file.descriptor?.status?.id !== "completed") {
    return intl.formatMessage({
      id: "publication.map.layermodal.disabled.descriptor_not_completed",
      defaultMessage: "The file is still loading or invalid and cannot be added (yet)",
    });
  }

  if (mode === "study-area" && !isStudyAreaFile(file))
    return intl.formatMessage({
      id: "publication.map.layermodal.disabled.not_study_area",
      defaultMessage: "This file is not a study area file",
    });

  if (mode === "layers" && isStudyAreaFile(file))
    return intl.formatMessage({
      id: "publication.map.layermodal.disabled.study_area",
      defaultMessage: "Study area files cannot be added as a map layer",
    });

  if (!isAbleToBeVisualized(file)) {
    if (isModelSchematisation(file)) {
      return intl.formatMessage({
        id: "publication.map.layermodal.disabled.model_schematisation_not_visualizable_yet",
        defaultMessage:
          "Model schematisation files cannot be visualized on the map yet, but will be in the future",
      });
    }
    return intl.formatMessage({
      id: "publication.map.layermodal.disabled.not_visualizable",
      defaultMessage: "This file type cannot be shown on the map",
    });
  }
};

const getEmptyMessage = ({
  intl,
  mode,
  hasFiles,
  searchString,
  hideDisabled,
}: {
  intl: IntlShape;
  mode: FilesTableMode;
  hasFiles: boolean;
  searchString: string;
  hideDisabled: boolean;
}) => {
  const isStudyArea = mode === "study-area";

  // Files exist but the search matches none of them
  if (hasFiles && searchString)
    return intl.formatMessage({
      id: "publication.map.layermodal.no_search_results",
      defaultMessage: "There are no files that match your search.",
    });

  // Files exist but all of them are unavailable and hidden
  if (hasFiles && hideDisabled)
    return isStudyArea
      ? intl.formatMessage({
          id: "publication.map.studyareamodal.no_available_files",
          defaultMessage: "None of the files in the publication are a study area.",
        })
      : intl.formatMessage({
          id: "publication.map.layermodal.no_available_files",
          defaultMessage:
            "None of the files in the publication can be shown on the map.\n\nShow the unavailable files (and hover on them) to see why.",
        });

  // The publication has no files at all
  return isStudyArea
    ? intl.formatMessage({
        id: "publication.map.studyareamodal.no_files",
        defaultMessage: "There are no study area files in the publication yet.",
      })
    : intl.formatMessage({
        id: "publication.map.layermodal.no_files",
        defaultMessage: "There are no files in the publication that can be shown on the map yet.",
      });
};

export const useFilesTable = ({ mode }: { mode: FilesTableMode }) => {
  const intl = useIntl();
  const publicationFiles = usePublicationStore(getFiles);
  const [searchString, setSearchString] = useState("");
  const [hideDisabled, setHideDisabled] = useState(false);

  const filteredPublicationFiles = useMemo(() => {
    const filteredPublicationFiles = publicationFiles
      .filter((file) => getFileName(file).toLowerCase().includes(searchString.toLowerCase()))
      .map((file) => ({ file, disabledReason: getDisabledReason({ intl, file, mode }) }));

    if (hideDisabled) {
      return filteredPublicationFiles.filter(({ disabledReason }) => !disabledReason);
    }

    return filteredPublicationFiles;
  }, [intl, mode, publicationFiles, searchString, hideDisabled]);

  const emptyMessage = getEmptyMessage({
    intl,
    mode,
    hasFiles: publicationFiles.length > 0,
    searchString,
    hideDisabled,
  });

  const columnDefinitions: ColumnDefinition[] = [
    {
      // checkboxes
      title: "",
      width: TABLE_SELECTION_COLUMN_WIDTH,
    },
    {
      title: intl.formatMessage({
        id: "publication.files.column.name",
        defaultMessage: "Name",
      }),
      width: "table_cell_xs",
    },
    {
      title: intl.formatMessage({
        id: "publication.files.column.size",
        defaultMessage: "Size",
      }),
      width: "table_cell_xs",
    },
    {
      title: intl.formatMessage({
        id: "publication.files.column.version",
        defaultMessage: "Version",
      }),
      width: "table_cell_xs",
    },
    {
      title: intl.formatMessage({
        id: "publication.files.column.added_by",
        defaultMessage: "Added by",
      }),
      width: "table_cell_xs",
    },
    {
      title: intl.formatMessage({
        id: "publication.files.column.last_modified",
        defaultMessage: "Last modified",
      }),
      width: "table_cell_xs",
    },
  ];

  return {
    filteredPublicationFiles,
    emptyMessage,
    columnDefinitions,
    searchString,
    setSearchString,
    hideDisabled,
    setHideDisabled,
  };
};
